import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MessageSquare, 
  Send, 
  User, 
  Crown, 
  Users,
  Clock
} from 'lucide-react';
import { useFeedbackThread, FeedbackReply } from '@/hooks/useFeedbackThread';
import { cn } from '@/lib/utils';

interface FeedbackThreadProps {
  feedbackId: string;
  feedbackMessage: string;
  feedbackType: string;
  feedbackPriority: string;
  feedbackStatus: string;
  eventName: string;
  userRole: 'client' | 'team_member' | 'event_planner';
  userName?: string;
  authorName?: string;
  createdAt: string;
  canReply?: boolean;
}

export const FeedbackThread = ({
  feedbackId,
  feedbackMessage,
  feedbackType,
  feedbackPriority,
  feedbackStatus,
  eventName,
  userRole,
  userName,
  authorName,
  createdAt,
  canReply = true
}: FeedbackThreadProps) => {
  const { replies, loading, addReply } = useFeedbackThread({
    feedbackId,
    userRole,
    userName
  });
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSendReply = async () => {
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      await addReply(newMessage);
      setNewMessage('');
    } catch (error) {
      // Error handled in hook
    } finally {
      setSending(false);
    }
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'event_planner':
        return (
          <Badge variant="default" className="bg-primary text-primary-foreground text-xs">
            <Crown className="h-3 w-3 mr-1" />
            Planner
          </Badge>
        );
      case 'client':
        return (
          <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-xs">
            <User className="h-3 w-3 mr-1" />
            Client
          </Badge>
        );
      case 'team_member':
        return (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs">
            <Users className="h-3 w-3 mr-1" />
            Team
          </Badge>
        );
      default:
        return null;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      default: return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{eventName}</CardTitle>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs">
                {feedbackType.replace('_', ' ')}
              </Badge>
              <Badge className={cn('text-xs', getPriorityColor(feedbackPriority))}>
                {feedbackPriority}
              </Badge>
              <Badge variant={feedbackStatus === 'open' ? 'default' : 'secondary'} className="text-xs">
                {feedbackStatus}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MessageSquare className="h-3 w-3" />
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col space-y-4">
        <ScrollArea className="flex-1 max-h-[400px]">
          <div className="space-y-4 pr-4">
            {/* Original feedback message */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                {getRoleBadge('client')}
                <span className="text-sm font-medium">{authorName || 'Client'}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {formatDate(createdAt)}
                </span>
              </div>
              <div className="bg-muted rounded-lg p-3 ml-0">
                <p className="text-sm">{feedbackMessage}</p>
              </div>
            </div>

            {/* Replies */}
            {loading ? (
              <div className="text-center py-4 text-muted-foreground text-sm">
                Loading conversation...
              </div>
            ) : (
              replies.map((reply) => (
                <div key={reply.id} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    {getRoleBadge(reply.author_role)}
                    <span className="text-sm font-medium">{reply.author_name}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(reply.created_at)}
                    </span>
                  </div>
                  <div className={cn(
                    "rounded-lg p-3",
                    reply.author_role === 'event_planner' 
                      ? "bg-primary/10 border-l-2 border-primary" 
                      : reply.author_role === 'team_member'
                        ? "bg-yellow-50 dark:bg-yellow-900/10 border-l-2 border-yellow-500"
                        : "bg-muted"
                  )}>
                    <p className="text-sm">{reply.message}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Reply input */}
        {canReply && (
          <div className="flex gap-2 pt-2 border-t">
            <Textarea
              placeholder="Type your reply..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              rows={2}
              className="flex-1 resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendReply();
                }
              }}
            />
            <Button 
              onClick={handleSendReply} 
              disabled={!newMessage.trim() || sending}
              size="icon"
              className="self-end"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackThread;
